import { ModelViewer } from './modules/ModelViewer.js';
import { ChatController } from './modules/ChatController.js';
import { CharacterSelector } from './modules/CharacterSelector.js';
import { ImageGalleryController } from './modules/ImageGalleryController.js';
import { BioController } from './modules/BioController.js';
import { ChatPositionManager } from './modules/ChatPositionManager.js';
import { ChatHistoryManager } from './modules/ChatHistoryManager.js';
import { ChatStorageManager } from './modules/ChatStorageManager.js';
import { ImageViewerModal } from './modules/ImageViewerModal.js';
import { AuthController } from './modules/AuthController.js';
import { characterData } from './modules/characterData.js';
import backendSubscriptionChecker from './modules/BackendSubscriptionChecker.js';
import skyModeController from './modules/SkyModeController.js';
import { ChatDragController } from './modules/ChatDragController.js';
import { TextureUpdater } from './modules/TextureUpdater.js';
import { installGreetingAudioUnlock } from './modules/GreetingAudioUnlock.js';


// RangeBasedFBXLoader will be imported in ModelViewer.js

// Make characterData globally available for ChatController
window.characterData = characterData;

// Task #519 — install the greeting audio-unlock kit at /lab boot, replacing
// the retired Safari "May <legend> read their first words to you?" popup.
// From this moment every tap/click/keypress is harvested (capture phase,
// removed once the greeting commits): inside that gesture the realtime-TTS
// AudioContext is resumed and the persistent greeting element is blessed
// with a REAL silent clip, so whichever greeting path later runs is already
// audible. See GreetingAudioUnlock.js for the three-path contract.
installGreetingAudioUnlock();

// Task #26: disarm the head-watchdog (Task #22) at module-level, as
// soon as the bundle has parsed and every static import has resolved.
// That is the only thing the watchdog is qualified to verify — the
// 25s timer must not measure DOMContentLoaded, Firebase init, model
// download time, or any other post-execute work. The script-error
// listener in templates/index.html still catches genuine asset load
// failures (429, network, broken bundle URL); the 25s timer still
// fires if the bundle never executes at all.
try {
    if (typeof window !== 'undefined' && typeof window.__labBootstrapDone === 'function') {
        window.__labBootstrapDone();
        try {
            (window.__labBootMarks = window.__labBootMarks || {}).bundleExecuting = performance.now();
            console.log('[LabBoot] +' + Math.round(performance.now()) + 'ms bundle_executing (watchdog disarmed)');
        } catch (_) {}
    }
} catch (_) {}

document.addEventListener('DOMContentLoaded', async () => {
    // Task #22: coarse per-step timing logs. When the page hangs we
    // need to know how far we got. All marks are relative to
    // `performance.timeOrigin` so they line up with the head-side
    // [LabBoot] markers from the dynamic bundle loader.
    const _bootMark = (label) => {
        try {
            const t = Math.round(performance.now());
            (window.__labBootMarks = window.__labBootMarks || {})[label] = performance.now();
            console.log('[LabBoot] +' + t + 'ms ' + label);
        } catch (_) {}
    };
    _bootMark('domContentLoaded');

    const errorOverlay = document.getElementById('error-overlay');
    const loadingOverlay = document.getElementById('loading-overlay');
    const loadingText = document.getElementById('loading-text');
    const viewer = document.getElementById('viewer');

    // Check for preselected model ID from Flask template (set when accessing via /lab/character_name)
    const preselectedModelId = window.PRESELECTED_MODEL_ID;
    
    // Also check URL query parameters for backward compatibility
    const urlParams = new URLSearchParams(window.location.search);
    const modelIdFromUrl = urlParams.get('model') || urlParams.get('character');
    
    const explicitModelId = preselectedModelId || modelIdFromUrl;
    const explicitEntry =
        window.__LAB_EXPLICIT_ENTRY === true || Boolean(explicitModelId);
    if (typeof window.__LAB_EXPLICIT_ENTRY !== 'boolean') {
        window.__LAB_EXPLICIT_ENTRY = explicitEntry;
    }

    // Bare /lab is the Hall's front door. A prior selection is useful only for
    // the explicit Continue card and must never be interpreted as permission
    // to load a model. The head guard runs before app.js; this repeat is
    // deliberate defense in depth around the boot-order contract.
    const savedSelection = localStorage.getItem('selectedModelId');
    if (!explicitEntry) {
        localStorage.removeItem('selectedModelId');
    }
    const existingSelection = explicitEntry ? savedSelection : null;

    // An EXPLICIT character in the URL (path slug via Flask preselection, or
    // ?model / ?character query param) always wins over any saved selection.
    // Clicking legend B's shared link must load B even if A was selected
    // previously on this device.
    if (explicitModelId) {
        if (existingSelection && existingSelection !== explicitModelId) {
            console.log('Explicit URL character', explicitModelId, 'overrides saved selection', existingSelection);
        } else {
            console.log('Explicit URL character selected:', explicitModelId);
        }
        localStorage.setItem('selectedModelId', explicitModelId);
        localStorage.setItem('forceKeepOpen', 'true');
    }
    if (modelIdFromUrl) {
        // Demo capture / deep links: when any demo param is present the URL is
        // the founder's reproducible shot — keep it intact (incl. ?character=)
        // so a refresh reproduces the exact same screen.
        const deepLinkParams = ['demo', 'hour', 'panel', 'book', 'page', 'museum'];
        const keepUrl = deepLinkParams.some((k) => urlParams.has(k));
        if (!keepUrl) {
            // Strip the query params so they can't override future user
            // selections (e.g. after switching characters in the Hall).
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('model');
            newUrl.searchParams.delete('character');
            window.history.replaceState({}, '', newUrl);
        }
    }

    const showError = (error) => {
        console.error('Application error:', error);
        const errorMessage = error.message || error.toString();
        if (errorOverlay) {
            errorOverlay.classList.remove('d-none');
            const alertElement = errorOverlay.querySelector('.alert');
            if (alertElement) {
                alertElement.textContent = `Error: ${errorMessage}`;
            }
        }
        if (loadingOverlay) {
            loadingOverlay.style.display = 'none';
        }
    };

    try {
        if (!viewer) {
            console.log('Viewer canvas not found — skipping Lab page initialization (likely on home page).');
            if (loadingOverlay) loadingOverlay.style.display = 'none';
            return;
        }

        console.log('Starting application initialization...');
        _bootMark('initStart');
        if (loadingText) loadingText.textContent = 'Initializing application...';

        // Initialize character selector
        new CharacterSelector();
        console.log('Character selector initialized');

        // Create ModelViewer instance and make it globally accessible
        const modelViewer = new ModelViewer(viewer);
        // Store in window for global access
        window.modelViewer = modelViewer;
        // Also store in the canvas element for component access
        viewer.__modelViewer = modelViewer;
        // Make ModelManager globally accessible for ChatController
        window.ModelManager = modelViewer.modelManager;
        console.log('ModelViewer instance created and made globally accessible');

        try {
            // Initialize the viewer
            _bootMark('modelViewerInitStart');
            await modelViewer.initialize();
            _bootMark('modelViewerInitDone');
            console.log('ModelViewer initialization completed');
            
            // Initialize Sky Mode Controller for Light/Dark mode transitions
            skyModeController.initialize();
            window.SkyModeController = skyModeController;
            console.log('Sky Mode Controller initialized for Light/Dark mode');

            // Initialize LipSync controller
            const lipSyncController = await modelViewer.getLipSyncController();
            console.log('LipSync initialization status:', lipSyncController ? 'success' : 'disabled');

            // Initialize chat controller (with or without LipSync)
            let chatController;
            if (lipSyncController) {
                chatController = new ChatController(lipSyncController, modelViewer);
                chatController.enableChat();
                console.log('Chat controller initialized with LipSync support');
            } else {
                // Initialize ChatController without LipSync for basic chat and RealtimeTTS functionality
                chatController = new ChatController(null, modelViewer);
                chatController.enableChat();
                console.log('Chat controller initialized without LipSync (basic mode)');
                console.warn('LipSync features will be disabled - chat and RealtimeTTS will still work');
            }
            
            // Initialize backend subscription checker first
            console.log('[Main] Initializing backend subscription checker...');
            await backendSubscriptionChecker.initPromise.catch(error => {
                console.error('[Main] Failed to initialize backend subscription checker:', error);
            });
            
            // Pre-initialize question counter if user is already authenticated
            if (window.firebaseAuth?.currentUser) {
                console.log('[Main] User already authenticated on page load, pre-initializing question counter');
                
                // Check subscription status first
                await backendSubscriptionChecker.refreshStatus().catch(error => {
                    console.error('[Main] Failed to check subscription status:', error);
                });
                
                chatController.questionCounter.initializeForUser(window.firebaseAuth.currentUser.uid).then(() => {
                    console.log('[Main] Question counter pre-initialized on page load');
                }).catch(error => {
                    console.error('[Main] Failed to pre-initialize question counter:', error);
                });
            } else {
                console.log('[Main] No authenticated user on page load, will initialize question counter on auth');
            }
            
            // On-demand AEC test for devs (Task #530: never auto-runs, never
            // posts into the chat thread; the WebRTC manager is retired)
            window.testAEC = () => chatController.testAECEffectiveness();
            
            // Connect ChatController to GreetingService for conversation history
            if (modelViewer.greetingService && modelViewer.greetingService.setChatController) {
                modelViewer.greetingService.setChatController(chatController);
                console.log('ChatController connected to GreetingService for conversation history');
            }

            // Initialize image gallery controller
            if (chatController) {
                new ImageGalleryController(chatController);
                console.log('Image gallery controller initialized');
            }
            
            // Initialize Bio controller and make it globally accessible
            const bioController = new BioController();
            // Make the bioController instance available globally for other components
            window.bioController = bioController;
            console.log('Bio controller initialized and exposed globally');
            
            // Initialize Authentication Controller for Sign In with Apple and other auth methods
            try {
                console.log('🔧 Attempting to create AuthController...');
                const authController = new AuthController();
                window.AuthController = authController;  // Changed to capital A to match onclick handler
                window.authController = authController;  // Keep lowercase for backward compatibility
                console.log('✅ Auth controller initialized successfully');
            } catch (error) {
                console.error('❌ Failed to initialize AuthController:', error);
                console.error('❌ Error details:', error.message, error.stack);
            }
            
            // Initialize Chat Position Manager for automatic chat window positioning
            const chatPositionManager = new ChatPositionManager();
            console.log('Chat position manager initialized');
            
            // Initialize Chat Drag Controller — a "drawer" that resizes the
            // VISIBLE "Tuscan dusk" chat (.chat-zone). The input + chips stay
            // pinned at the bottom; only the message thread (.chat-thread)
            // grows/shrinks so the user can uncover the 3D character's face
            // without ever pushing the input off-screen.
            const chatDragController = new ChatDragController({
                chatSelector: '.chat-zone',
                threadSelector: '.chat-thread',
                onResize: (data) => {
                    window.dispatchEvent(new CustomEvent('chatThreadResized', { detail: data }));
                }
            });
            window.chatDragController = chatDragController;
            console.log('Chat drag controller initialized');
            
            // Initialize Chat History Manager for viewing and managing stored conversations
            const chatHistoryManager = new ChatHistoryManager();
            window.chatHistoryManager = chatHistoryManager;
            console.log('Chat history manager initialized');
            
            // Initialize Image Viewer Modal for in-app image viewing with parallel chat
            const imageViewerModal = new ImageViewerModal();
            window.imageViewerModal = imageViewerModal;
            // Connect the modal to the chat controller if available
            if (chatController) {
                imageViewerModal.setChatController(chatController);
                window.chatController = chatController; // Make chatController globally accessible
            }
            console.log('Image viewer modal initialized');
            
            // History button: the new "Tuscan dusk" slide-in history panel
            // (#history-panel / body.history-open) owns the #history-button click
            // (wired inline in templates/index.html, where it renders the REAL
            // Firebase chat history via window.chatHistoryManager into the new UI).
            // The legacy left-side #chat-history-panel stays created as a data
            // source/safety net but must NOT be opened here — opening it via
            // togglePanel() caused TWO panels (left + right) to appear at once.
            
            // Set character data for Bio controller when character changes
            // Get the current model ID (which should be the current character)
            const currentModelId = localStorage.getItem('selectedModelId');
            if (currentModelId && characterData[currentModelId]) {
                    const character = characterData[characterId];
                bioController.setCharacter(
                    currentModelId,
                    character.name,
                    character.imageUrl
                );
                console.log('Bio controller set to character:', character.name);
                
                // Force a character-changed event to initialize all components with bio data
                const initialCharacterEvent = new CustomEvent('character-changed', {
                    detail: {
                        modelId: currentModelId,
                        name: character.name,
                        imageUrl: character.imageUrl
                    },
                    bubbles: true
                });
                document.dispatchEvent(initialCharacterEvent);
                console.log('Initial character-changed event dispatched for:', character.name);
            }
            
            // Add listener for character change events
            document.addEventListener('character-changed', (event) => {
                const characterId = event.detail.modelId;
                if (characterId && characterData[characterId]) {
                    const character = characterData[characterId];
                    bioController.setCharacter(
                        characterId,
                        character.name,
                        character.imageUrl
                    );
                    console.log('Bio controller updated to character:', character.name);
                    
                    // Force an update to the bio data when character changes
                    bioController.loadBioData();
                    console.log('Bio data force-updated for character:', character.name);
                }
            });

            const textureUpdater = new TextureUpdater();
            textureUpdater.init();
            window.textureUpdater = textureUpdater;
            console.log('Texture updater initialized');

            _bootMark('initComplete');
            console.log('Application initialization completed successfully');
            if (loadingOverlay) loadingOverlay.style.display = 'none';
            // Task #26: watchdog is already disarmed at module-level
            // (top of this file). No redundant call needed here.

        } catch (initError) {
            console.error('Initialization error:', initError);
            throw new Error(`Initialization failed: ${initError.message}`);
        }

    } catch (error) {
        showError(error);
    }
});
